<script setup lang="ts">
/**
 * Top nav shown across every marketing page. Sticky on scroll.
 * Brand name is intentionally placeholder ("Caseflow") — swap once the
 * final rebrand lands. Pages are stable.
 *
 * Dropdown closes when:
 *   - Any item inside it is clicked (Vue's @click on NuxtLink fires before navigation)
 *   - The dropdown button is toggled
 *   - A native click anywhere outside (via a document listener)
 */
const verticalsOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const verticals = [
  { slug: 'migration-agencies', label: 'Migration agencies' },
  { slug: 'education-agencies', label: 'Education agencies' },
  { slug: 'law-firms', label: 'Law firms' },
  { slug: 'real-estate-agencies', label: 'Real estate agencies' },
]

const onDocumentClick = (e: MouseEvent) => {
  if (!verticalsOpen.value) return
  const el = dropdownRef.value
  if (el && !el.contains(e.target as Node)) {
    verticalsOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onDocumentClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick))
</script>

<template>
  <header class="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
    <div class="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
      <NuxtLink to="/" class="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <span class="inline-block w-7 h-7 rounded bg-gradient-to-br from-indigo-500 to-purple-500"></span>
        Caseflow
      </NuxtLink>

      <nav class="hidden md:flex items-center gap-7 text-sm text-gray-700">
        <div ref="dropdownRef" class="relative">
          <button @click="verticalsOpen = !verticalsOpen" class="flex items-center gap-1 hover:text-gray-900">
            Verticals
            <svg width="12" height="12" viewBox="0 0 12 12" class="opacity-60"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
          <div v-if="verticalsOpen" class="absolute right-0 top-full mt-1 w-60 rounded-lg border border-gray-200 bg-white shadow-lg p-2">
            <NuxtLink
              v-for="v in verticals" :key="v.slug"
              :to="`/${v.slug}`" class="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              @click="verticalsOpen = false"
            >{{ v.label }}</NuxtLink>
          </div>
        </div>
        <NuxtLink to="/demos" class="hover:text-gray-900">Demos</NuxtLink>
        <NuxtLink to="/pricing" class="hover:text-gray-900">Pricing</NuxtLink>
        <NuxtLink to="/blog" class="hover:text-gray-900">Blog</NuxtLink>
        <NuxtLink to="/contact" class="hover:text-gray-900">Contact</NuxtLink>
        <NuxtLink to="/login" class="hover:text-gray-900">Sign in</NuxtLink>
        <NuxtLink to="/signup" class="rounded-lg bg-indigo-600 text-white px-4 py-1.5 hover:bg-indigo-700">Start free</NuxtLink>
      </nav>
    </div>
  </header>
</template>
